import { useMemo } from 'react';

import { isFilter, Filter, AllowedOperators } from '@/src/util/filters';

import useSearchParams, { SearchParams } from '../useSearchParamsState';

export const parseFilters = (raw: string | null): Filter[] => {
  if (!raw) return [];
  try {
    return JSON.parse(raw) as Filter[];
  } catch (e) {
    console.warn('Invalid filter param', e);
    return [];
  }
};

export const upsertFieldFilter = (
  filters: Filter[] = [],
  field: string,
  label: string,
  values: (string | number)[] | undefined
): Filter[] => {
  const withoutField = filters.filter((f) => f.field !== field);
  if (!values || values.length === 0) return withoutField;

  return [
    ...withoutField,
    { field, label, conditions: [{ op: AllowedOperators.IN, val: values }] },
  ];
};

export const useBaseFilterSearchParams = () => {
  const [searchParams, setSearchParamsState, clearSearchParams] = useSearchParams();

  const currentSearchParams = useMemo(() => {
    const obj: SearchParams = {};
    searchParams.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }, [searchParams]);

  const parsedFilter = useMemo(() => parseFilters(searchParams.get('filter')), [searchParams]);

  const setParams = (params: Record<string, unknown>) => {
    const newParams = { ...currentSearchParams };

    Object.entries(params).forEach(([key, value]) => {
      if (key === 'filter') {
        if (value === undefined) {
          newParams[key] = undefined;
          return;
        }

        const currentFilters = parseFilters(searchParams.get('filter'));
        let nextFilters: Filter[] | undefined = currentFilters;

        if (Array.isArray(value)) {
          nextFilters = value as Filter[];
        } else if (isFilter(value)) {
          nextFilters = upsertFieldFilter(
            currentFilters,
            value.field,
            value.label ?? '',
            value.conditions[0]?.val as any
          );
        }

        newParams[key] = nextFilters?.length ? JSON.stringify(nextFilters) : undefined;
      } else {
        newParams[key] = value != null ? String(value) : undefined;
      }
    });

    setSearchParamsState(newParams);
  };

  return {
    searchParams,
    currentSearchParams,
    parsedFilter,
    setParams,
    clearSearchParams,
  };
};
